import { useState } from 'react'
import { describeOutsideEvidence, isRole, roleHasCapability } from '@qim/shared'
import { ApiError, markDestroyedOutside } from '../api.js'
import { formatInstant } from '../format.js'
import type { DeploymentSummary } from '../api.js'
import type { JSX } from 'react'

/**
 * An instance destroyed outside the control plane (IM-156).
 *
 * Two states, one component, because they are the before and after of one
 * decision: the scan says the cluster is gone and offers to mark it; once
 * marked, the screen says who decided and offers to take it back.
 *
 * **No confirmation dialog.** Destroy is the only command that asks first
 * (IM-79), because it is the only irreversible one. This changes a record and
 * is withdrawn in one click, and a dialog here would train people to dismiss
 * the one that matters.
 *
 * **No verdict.** It reports what the scan found and when — it never calls the
 * instance orphaned, stale or wasted, the rule every other screen keeps.
 */
export const OutsideNotice = ({
  deployment,
  baseUrl,
  tenantId,
  role,
  onChanged,
}: {
  readonly deployment: Pick<
    DeploymentSummary,
    'deploymentId' | 'accountKey' | 'destroyedOutside' | 'scanSaysGone'
  >
  readonly baseUrl: string
  readonly tenantId: string
  readonly role: string
  readonly onChanged: () => void
}): JSX.Element | null => {
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | undefined>(undefined)
  const { destroyedOutside: mark, scanSaysGone: gone } = deployment
  if (mark === undefined && gone === undefined) return null

  // Not a control — the API checks `records:manage` and records the decision
  // either way — but a button that always fails is a worse way to say no.
  const canMark = isRole(role) && roleHasCapability(role, 'records:manage')

  const act = async (withdraw: boolean): Promise<void> => {
    setBusy(true)
    setProblem(undefined)
    try {
      await markDestroyedOutside(
        baseUrl,
        tenantId,
        deployment.accountKey,
        deployment.deploymentId,
        withdraw,
      )
      // Re-read rather than splice: the status is the API's to derive.
      onChanged()
    } catch (error: unknown) {
      setProblem(
        error instanceof ApiError && error.status === 409
          ? 'The control plane refused that — the account may have been scanned again since this screen loaded. Reload and look again.'
          : error instanceof ApiError && error.status === 403
            ? 'Your role does not permit that (records:manage).'
            : 'Could not save that just now.',
      )
    } finally {
      setBusy(false)
    }
  }

  if (mark !== undefined) {
    return (
      <div className="outside-note" role="status">
        <p>
          <strong>Destroyed outside the control plane.</strong> Marked by {mark.by} on{' '}
          {formatInstant(mark.at)}, on the scan of {formatInstant(mark.scannedAt)}, which found{' '}
          {describeOutsideEvidence(mark.evidence)}. Nothing here ran a destroy, so nothing here
          knows what was removed.
        </p>
        {canMark ? (
          <button type="button" className="ghost" disabled={busy} onClick={() => void act(true)}>
            Withdraw the mark
          </button>
        ) : null}
        {problem === undefined ? null : (
          <p role="alert" className="failure">
            {problem}
          </p>
        )}
      </div>
    )
  }

  if (gone === undefined) return null
  return (
    <div className="outside-note" role="status">
      <p>
        <strong>The last scan could not find this instance&rsquo;s cluster.</strong> The scan of{' '}
        {formatInstant(gone.scannedAt)} found {describeOutsideEvidence(gone.evidence)}
        {gone.since === undefined
          ? ''
          : `, and has not seen the cluster since ${formatInstant(gone.since)}`}
        . If it was destroyed outside this control plane, mark it so; it will read as destroyed and
        can then be removed. A scan that could not read the region looks the same, so check before
        marking.
      </p>
      {canMark ? (
        <button type="button" className="ghost" disabled={busy} onClick={() => void act(false)}>
          Mark destroyed
        </button>
      ) : (
        <p className="hint">Your role does not permit marking it (records:manage).</p>
      )}
      {problem === undefined ? null : (
        <p role="alert" className="failure">
          {problem}
        </p>
      )}
    </div>
  )
}

/** The one-word version, for a table cell. */
export const OutsideHint = ({
  deployment,
}: {
  readonly deployment: Pick<DeploymentSummary, 'destroyedOutside' | 'scanSaysGone'>
}): JSX.Element | null =>
  deployment.destroyedOutside !== undefined ? (
    <p className="muted">outside the control plane</p>
  ) : deployment.scanSaysGone !== undefined ? (
    <p className="muted">scan finds no cluster</p>
  ) : null
